/** AI Import — a draft's source: the crawled page (a plain link), or the uploaded
 *  document it was extracted from (`upload://<filename>`), downloaded through a
 *  short-lived presigned link the backend signs on click. */
import DownloadIcon from '@mui/icons-material/Download';
import { CircularProgress, Link } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import type { MouseEvent, ReactNode } from 'react';

import * as aiImportApi from '@/api/aiImport';
import type { AiImportDocument } from '@/api/aiImport';
import { useSnackbar } from '@/components/Snackbar';

export function SourceLink({
  taskId,
  sourceUrl,
  documents,
  children,
  variant = 'inherit',
}: {
  taskId: string;
  sourceUrl: string;
  documents: AiImportDocument[];
  /** Link text for a crawled page (default: the URL without its scheme). */
  children?: ReactNode;
  variant?: 'inherit' | 'body2';
}) {
  const { show } = useSnackbar();
  const doc = documents.find((d) => d.source_url === sourceUrl);
  const download = useMutation({
    mutationFn: () => aiImportApi.documentUrl(taskId, doc!.index),
    onSuccess: (r) => window.open(r.url, '_blank', 'noopener'),
    onError: (e) => show(`Could not download ${doc?.filename}: ${(e as Error).message}`, 'error'),
  });

  if (!sourceUrl.startsWith('upload://')) {
    return (
      <Link href={sourceUrl} target="_blank" rel="noopener noreferrer" variant={variant}>
        {children ?? sourceUrl.replace(/^https?:\/\//, '')}
      </Link>
    );
  }
  const filename = doc?.filename ?? sourceUrl.slice('upload://'.length);
  if (!doc) return <span>{filename}</span>;
  return (
    <Link
      component="button"
      type="button"
      variant={variant}
      title={`Download ${filename}`}
      disabled={download.isPending}
      onClick={(e: MouseEvent) => {
        // Grid rows open the detail drawer on click; a download is not that.
        e.stopPropagation();
        download.mutate();
      }}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'baseline' }}
    >
      {download.isPending ? <CircularProgress size={12} /> : <DownloadIcon sx={{ fontSize: 14 }} />}
      {filename}
    </Link>
  );
}
